const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
  'loading chunk',
  'loading css chunk',
];

/**
 * Detects errors thrown when a lazily-imported chunk cannot be fetched.
 * This typically happens after a new deploy invalidates the old chunk URLs
 * while the user is still running the previous build.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;

  const message = error.message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

const CHUNK_RELOAD_KEY = 'med|chunk-reload-at';
const CHUNK_RELOAD_WINDOW_MS = 10_000;

/**
 * Reloads the page once to pick up the latest chunks after a deploy.
 * Guards against an infinite reload loop by recording the last reload time:
 * if a reload already happened within the recent window, it is suppressed so
 * the caller can fall back to a user-facing error message instead.
 *
 * @returns `true` if a reload was triggered, `false` if it was suppressed.
 */
export const reloadOnChunkError = (): boolean => {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
    const isRecentReload = Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < CHUNK_RELOAD_WINDOW_MS;

    if (isRecentReload) return false;

    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch (_e) {
    // sessionStorage may be unavailable (e.g. private mode); fail safe to the fallback UI.
    return false;
  }
};
